import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase-client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart3, Users, Briefcase, Calendar, TrendingUp, Plus } from 'lucide-react';

interface RecruiterMetrics {
  totalActiveJobs: number;
  totalApplications: number;
  pendingReviews: number;
  applicationsThisWeek: number;
  interviewsCompleted: number;
  averageCandidateScore: number;
}

interface RecentActivity {
  id: string;
  type: 'application' | 'interview' | 'job_created';
  candidateName?: string;
  jobTitle: string;
  timestamp: string;
  score?: number;
}

interface ActiveJob {
  id: string;
  title: string;
  location: string;
  applicationCount: number;
  createdAt: string;
}

export default function RecruiterDashboard() {
  const navigate = useNavigate();
  const { getOrganizationId, getOrganizationName, organizationData } = useAuth();
  const [metrics, setMetrics] = useState<RecruiterMetrics>({
    totalActiveJobs: 0,
    totalApplications: 0,
    pendingReviews: 0,
    applicationsThisWeek: 0,
    interviewsCompleted: 0,
    averageCandidateScore: 0,
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [activeJobs, setActiveJobs] = useState<ActiveJob[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      const organizationId = getOrganizationId();
      if (!organizationId) {
        setLoading(false);
        return;
      }

      try {
        const { data: jobs, error: jobsError } = await supabase
          .from('job_postings')
          .select('id, title, location, created_at')
          .eq('organization_id', organizationId)
          .order('created_at', { ascending: false });

        if (jobsError) throw jobsError;

        const jobIds = jobs?.map((job) => job.id) || [];

        const { data: recruiterProfiles, error: recruiterProfilesError } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('is_recruiter', true);

        if (recruiterProfilesError) throw recruiterProfilesError;

        const recruiterUserIds = recruiterProfiles?.map((profile) => profile.id) || [];

        const { data: sessions, error: sessionsError } = await supabase
          .from('interview_sessions')
          .select('id, job_posting_id, user_id, created_at, ai_evaluation, candidate_name, status')
          .in('job_posting_id', jobIds)
          .order('created_at', { ascending: false });

        if (sessionsError) throw sessionsError;

        const candidateSessions = sessions?.filter(
          (session) => !session.user_id || !recruiterUserIds.includes(session.user_id)
        ) || [];

        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

        const applicationsThisWeek = candidateSessions?.filter(
          (session) => new Date(session.created_at) >= oneWeekAgo
        ).length || 0;

        const completedInterviews = candidateSessions?.filter(
          (session: any) => session.ai_evaluation && session.status === 'completed'
        ) || [];

        const averageScore = completedInterviews.length > 0
          ? completedInterviews.reduce((sum, session: any) => {
              const score = session.ai_evaluation?.finalScore || session.ai_evaluation?.overall_score || session.ai_evaluation?.score || 0;
              return sum + score;
            }, 0) / completedInterviews.length
          : 0;

        const jobsWithApplications: ActiveJob[] = jobs?.map((job) => {
          const applicationCount = candidateSessions?.filter(
            (session) => session.job_posting_id === job.id
          ).length || 0;
          return {
            id: job.id,
            title: job.title,
            location: job.location,
            applicationCount,
            createdAt: job.created_at,
          };
        }) || [];

        const recentActivities: RecentActivity[] = [
          ...candidateSessions?.slice(0, 10).map((session: any) => ({
            id: session.id,
            type: 'interview' as const,
            candidateName: session.candidate_name || 'Anonymous',
            jobTitle: jobs?.find((job) => job.id === session.job_posting_id)?.title || 'Unknown Job',
            timestamp: session.created_at,
            score: session.ai_evaluation?.finalScore || session.ai_evaluation?.overall_score || session.ai_evaluation?.score || undefined,
          })) || [],
        ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

        setMetrics({
          totalActiveJobs: jobs?.length || 0,
          totalApplications: candidateSessions?.length || 0,
          pendingReviews: candidateSessions?.filter((s: any) => !s.ai_evaluation || s.status !== 'completed').length || 0,
          applicationsThisWeek,
          interviewsCompleted: completedInterviews.length,
          averageCandidateScore: averageScore,
        });

        setRecentActivity(recentActivities.slice(0, 5));
        setActiveJobs(jobsWithApplications.slice(0, 5));
      } catch (error) {
        console.error('Error fetching recruiter dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [getOrganizationId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Recruiter Dashboard</h1>
              <p className="text-gray-600 mt-1">
                {organizationData?.name || 'Your Organization'}
              </p>
            </div>
            <Button onClick={() => navigate('/jobs/create')} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Create New Job
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Jobs</CardTitle>
              <Briefcase className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.totalActiveJobs}</div>
              <p className="text-xs text-muted-foreground">Total open positions</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Applications</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.totalApplications}</div>
              <p className="text-xs text-muted-foreground">
                {metrics.applicationsThisWeek} this week
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Interviews Completed</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.interviewsCompleted}</div>
              <p className="text-xs text-muted-foreground">AI-evaluated interviews</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Reviews</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.pendingReviews}</div>
              <p className="text-xs text-muted-foreground">Awaiting evaluation</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Average Score</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.averageCandidateScore.toFixed(1)}%</div>
              <p className="text-xs text-muted-foreground">Across all candidates</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle>Active Job Postings</CardTitle>
              <CardDescription>Your open positions with application counts</CardDescription>
            </CardHeader>
            <CardContent>
              {activeJobs.length === 0 ? (
                <div className="text-center py-8">
                  <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 mb-4">No active jobs yet</p>
                  <Button onClick={() => navigate('/jobs/create')} variant="outline">
                    Create Your First Job
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {activeJobs.map((job) => (
                    <div
                      key={job.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition"
                      onClick={() => navigate(`/jobs/${job.id}`)}
                    >
                      <div>
                        <h3 className="font-semibold text-gray-900">{job.title}</h3>
                        <p className="text-sm text-gray-600">{job.location}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-blue-600">{job.applicationCount}</p>
                        <p className="text-xs text-gray-600">applications</p>
                      </div>
                    </div>
                  ))}
                  <Button
                    onClick={() => navigate('/jobs')}
                    variant="outline"
                    className="w-full"
                  >
                    View All Jobs
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Latest candidate interviews and applications</CardDescription>
            </CardHeader>
            <CardContent>
              {recentActivity.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No recent activity</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentActivity.map((activity) => (
                    <div
                      key={activity.id}
                      className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {activity.candidateName}
                        </p>
                        <p className="text-sm text-gray-600">{activity.jobTitle}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(activity.timestamp).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                      {activity.score !== undefined && (
                        <div className="text-right">
                          <p className="text-lg font-bold text-green-600">
                            {activity.score.toFixed(0)}%
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                  <Button
                    onClick={() => navigate('/candidates')}
                    variant="outline"
                    className="w-full"
                  >
                    View All Candidates
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Button
            onClick={() => navigate('/jobs/create')}
            className="h-24 text-lg"
            variant="outline"
          >
            <Plus className="w-5 h-5 mr-2" />
            Post New Job
          </Button>
          <Button
            onClick={() => navigate('/candidates')}
            className="h-24 text-lg"
            variant="outline"
          >
            <Users className="w-5 h-5 mr-2" />
            View Candidates
          </Button>
          <Button
            onClick={() => navigate('/interviews')}
            className="h-24 text-lg"
            variant="outline"
          >
            <BarChart3 className="w-5 h-5 mr-2" />
            Review Interviews
          </Button>
        </div>
      </div>
    </div>
  );
}
